import {
  useEffect,
  useId,
  useRef,
  useState,
  // Døpt om: `KeyboardEvent` uten navn ville skygget for nettleserens egen,
  // som `Modal` under bruker på `document`.
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';

export function Alert({
  kind = 'error',
  children,
}: {
  kind?: 'error' | 'success' | 'info';
  children: ReactNode;
}) {
  if (!children) return null;
  return (
    <div className={`alert alert-${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
      {children}
    </div>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <label className="field">
      <span className="label">{label}</span>
      {children}
      {hint && !error ? <span className="hint">{hint}</span> : null}
      {error ? <span className="field-error">{error}</span> : null}
    </label>
  );
}

export function Modal({
  title,
  description,
  onClose,
  children,
}: {
  title: string;
  description?: string;
  onClose: () => void;
  children: ReactNode;
}) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="modal" role="dialog" aria-modal="true" aria-label={title}>
        <h2>{title}</h2>
        {description ? <p className="muted small">{description}</p> : null}
        {children}
      </div>
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  children,
  action,
}: {
  /** Et <Ikon/>. Var tidligere et Unicode-tegn, som ble tegnet ulikt per enhet. */
  icon: ReactNode;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <span className="empty-icon">{icon}</span>
      <h3>{title}</h3>
      {children ? <p>{children}</p> : null}
      {action}
    </div>
  );
}

export function LoadingBlock({ label = 'Henter …' }: { label?: string }) {
  return (
    <div className="loading-page">
      <div className="spinner" aria-hidden="true" />
      <p className="small muted">{label}</p>
    </div>
  );
}

/**
 * Framdriftslinje.
 *
 * `label` er ikke valgfritt av latskap: en `progressbar` uten navn leses opp
 * som «framdrift, 6» uten at noen vet framdrift på hva. Alle fire stedene den
 * brukes, står den ved siden av et tall som allerede sier det – men en
 * skjermleser hopper mellom elementer, ikke langs setningen.
 */
export function ProgressBar({
  value,
  max,
  label,
}: {
  value: number;
  max: number;
  label: string;
}) {
  const ratio = max <= 0 ? 0 : value / max;
  return (
    <div
      className={`progress${ratio > 1 ? ' over' : ''}`}
      role="progressbar"
      aria-label={label}
      aria-valuenow={Math.round(value)}
      aria-valuemin={0}
      aria-valuemax={Math.round(max)}
    >
      <span style={{ width: `${Math.min(100, Math.max(0, ratio * 100))}%` }} />
    </div>
  );
}

/**
 * Tekstfelt med forslag fra det som allerede finnes.
 *
 * Husstander og grupper skrives inn for hånd, én gjest om gangen. Uten hjelp
 * blir «Familien Nordmann», «Fam. Nordmann» og «familien nordmann» tre
 * forskjellige grupper, og bordplasseringen tror de skal sitte hver for seg.
 * Forslagene er derfor ikke bare en snarvei – de er det som gjør at gruppene
 * faktisk henger sammen.
 *
 * Feltet er fritt: står gruppen ikke i lista, skriver man den bare inn.
 */
export function Combobox({
  value,
  onChange,
  suggestions,
  placeholder,
}: {
  value: string;
  onChange: (verdi: string) => void;
  suggestions: string[];
  placeholder?: string;
}) {
  const [apen, setApen] = useState(false);
  const [uthevet, setUthevet] = useState(-1);
  const listeId = useId();
  const listeRef = useRef<HTMLUListElement>(null);

  const treff = suggestions.filter(
    (forslag) => !value.trim() || forslag.toLowerCase().includes(value.trim().toLowerCase()),
  );
  const vis = apen && treff.length > 0;

  /**
   * Dialogen klipper det som stikker utenfor. Åpner lista seg nederst i et
   * skjema, havner halve under kanten, og forslagene finnes uten å være å se.
   * `nearest` ruller akkurat nok til å få den fram, og rører ingenting når den
   * alt er synlig.
   */
  useEffect(() => {
    if (vis) listeRef.current?.scrollIntoView({ block: 'nearest' });
  }, [vis]);

  // Lista rommer en håndfull om gangen. Går man nedover med pilene forbi det
  // som vises, må den følge etter – ellers flytter markeringen seg et sted man
  // ikke ser.
  useEffect(() => {
    if (uthevet < 0) return;
    listeRef.current?.children[uthevet]?.scrollIntoView({ block: 'nearest' });
  }, [uthevet]);

  const velg = (forslag: string) => {
    onChange(forslag);
    setApen(false);
    setUthevet(-1);
  };

  const taster = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setApen(false);
      return;
    }
    if (!vis) {
      if (event.key === 'ArrowDown') setApen(true);
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setUthevet((n) => (n + 1) % treff.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setUthevet((n) => (n <= 0 ? treff.length - 1 : n - 1));
    } else if (event.key === 'Enter' && uthevet >= 0) {
      // Bare når et forslag er valgt med tastene – ellers skal Enter sende
      // skjemaet, slik det gjør i alle de andre feltene.
      event.preventDefault();
      velg(treff[uthevet]!);
    }
  };

  return (
    <div className="kombiboks">
      <input
        className="input"
        role="combobox"
        aria-expanded={vis}
        aria-controls={listeId}
        aria-autocomplete="list"
        autoComplete="off"
        placeholder={placeholder}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
          setApen(true);
          setUthevet(-1);
        }}
        onFocus={() => setApen(true)}
        onBlur={() => setApen(false)}
        onKeyDown={taster}
      />
      {vis ? (
        <ul className="kombiboks-liste" id={listeId} role="listbox" ref={listeRef}>
          {treff.map((forslag, n) => (
            <li
              key={forslag}
              role="option"
              aria-selected={n === uthevet}
              className={n === uthevet ? 'er-uthevet' : undefined}
              // `mousedown` med `preventDefault` i stedet for `click`: ellers
              // rekker feltet å miste fokus, lista lukkes, og klikket lander
              // på ingenting.
              onMouseDown={(event) => {
                event.preventDefault();
                velg(forslag);
              }}
              onMouseEnter={() => setUthevet(n)}
            >
              {forslag}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
